import axios from "axios";
import type {
  InvoicePeriodCheck,
  InvoiceSyncReport,
  InvoiceSyncRunRequest,
  InvoiceSyncStatus,
  SubscriptionInvoicePage,
  SubscriptionInvoicesQuery,
} from "@/interfaces/invoiceSync";
import { apiClient } from "@/lib/apiClient";

/** Drops empty params so an unset search isn't sent as "". */
function cleanQuery(q: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = String(v);
  }
  return out;
}

/** Rethrows with the server's message, so a refusal reads as what it is. */
function serverError(err: unknown): never {
  if (axios.isAxiosError(err) && err.response?.data?.error) {
    throw new Error(String(err.response.data.error));
  }
  throw err;
}

/** Subscription invoices (billing cycles) for Finance › Invoices. GFG-only. */
export async function fetchSubscriptionInvoices(
  query: SubscriptionInvoicesQuery,
): Promise<SubscriptionInvoicePage> {
  const { data } = await apiClient.get<{ success: boolean; data: SubscriptionInvoicePage }>(
    "/admin/finance/subscription-invoices",
    { params: cleanQuery(query) },
  );
  return data.data;
}

/** What a sync over the range would do — both kinds of invoice. Writes nothing. */
export async function previewInvoiceSync(range: {
  from: string;
  to: string;
}): Promise<InvoiceSyncReport> {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: InvoiceSyncReport }>(
      "/admin/finance/invoice-sync/preview",
      range,
    );
    return data.data;
  } catch (err) {
    serverError(err);
  }
}

/** Runs the sync over the range: only the fixes and imports the person ticked. */
export async function runInvoiceSync(request: InvoiceSyncRunRequest): Promise<InvoiceSyncReport> {
  try {
    const { data } = await apiClient.post<{ success: boolean; data: InvoiceSyncReport }>(
      "/admin/finance/invoice-sync",
      request,
    );
    return data.data;
  } catch (err) {
    serverError(err);
  }
}

/** The last daily sync. */
export async function fetchInvoiceSyncStatus(): Promise<InvoiceSyncStatus> {
  const { data } = await apiClient.get<{ success: boolean; data: InvoiceSyncStatus }>(
    "/admin/finance/invoice-sync/status",
  );
  return data.data;
}

/** What would block closing a month. Null where the check does not apply (not a deployed service). */
export async function fetchInvoicePeriodCheck(period: string): Promise<InvoicePeriodCheck | null> {
  const { data } = await apiClient.get<{ success: boolean; data: InvoicePeriodCheck | null }>(
    "/admin/finance/invoice-sync/period-check",
    { params: { period } },
  );
  return data.data;
}
